/**
 * MobileCommunityOverlay — Full-screen overlay for viewing a specific community.
 * Shown when navigating to /community/:slug on mobile.
 * Renders the lightweight MobileCommunityClient inside a fixed overlay with back button.
 * Fetches and applies the community's theme so it doesn't use the site-wide theme.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { MobileCommunityClient } from './MobileCommunityClient';
import { useTheme } from '../../context/ThemeContext';
import { transformColor, type ColorTransformOptions } from '../../utils/colorTransform';
import { getBackground, getCardBackground, hexToRgba } from '../../utils/backgroundHelpers';
import { getThemeByKey, type PresetThemeKey, PRESET_THEMES } from '@theme/tokens';

import { fetchWithAuth } from '../../lib/fetchWithAuth';
interface MobileCommunityOverlayProps {
  slug: string;
}

/** Build CSS custom-property style object from a community's customization blob */
function buildThemeVars(raw: any): React.CSSProperties {
  if (!raw) return {};
  // Always merge with preset defaults (matching desktop CommunityClient behavior).
  // Desktop uses: { ...PRESET_THEMES['ocean-turtle'], ...community.communityCustomization }
  const presetKey = (raw.baseTheme && raw.baseTheme in PRESET_THEMES)
    ? raw.baseTheme as PresetThemeKey
    : 'ocean-turtle' as PresetThemeKey;
  const preset = getThemeByKey(presetKey);
  let c = { ...preset, ...raw };
  // Preserve preset card gradient if saved data doesn't override it
  if (preset.cardGradientEnabled && !raw.hasOwnProperty?.('cardGradientEnabled')) {
    c.cardGradientEnabled = true;
  }
  if (preset.cardGradientColor1 && !c.cardGradientColor1?.startsWith('#')) {
    c.cardGradientColor1 = preset.cardGradientColor1;
  }
  if (preset.cardGradientColor2 && !c.cardGradientColor2?.startsWith('#')) {
    c.cardGradientColor2 = preset.cardGradientColor2;
  }
  if (!c.primaryColor) return {};

  const colorSpace = c.colorSpace || 'Off';

  const hexToRgbaWithOpacity = (hex: string, opacity: number) => {
    if (!hex || !hex.startsWith('#') || hex.length < 7) return hex;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
  };

  let textColor = c.textColor;
  let accentColor = c.accentColor;
  let primaryColor = c.primaryColor;
  let secondaryColor = c.secondaryColor;
  let cardBg = c.cardBg;

  if (colorSpace !== 'Off') {
    const opts: ColorTransformOptions = {
      colorSpace,
      temperatureLimit: c.temperatureLimit ?? 50,
      magentaLimit: c.magentaLimit ?? 50,
      intensityLimit: c.intensityLimit ?? 50,
      saturationLimit: c.backgroundSaturationLimit ?? 50,
      brightnessLimit: c.backgroundBrightnessLimit ?? 50,
      redLimit: c.redLimit ?? 50,
      greenLimit: c.greenLimit ?? 50,
      blueLimit: c.blueLimit ?? 50,
      contrast: c.backgroundContrast ?? 50,
      hueGravity: c.backgroundHueGravity ?? 0,
      graySaturation: c.backgroundGraySaturation ?? 0,
      targetHue: c.backgroundDefaultHue ?? 184,
    };
    textColor = transformColor(textColor, opts);
    accentColor = transformColor(accentColor, opts);
    primaryColor = transformColor(primaryColor, opts);
    secondaryColor = transformColor(secondaryColor, opts);
    if (cardBg?.startsWith('#')) cardBg = transformColor(cardBg, opts);
  }

  const navbarBg = hexToRgbaWithOpacity(
    c.navbarBg?.startsWith('#') ? (colorSpace !== 'Off' ? transformColor(c.navbarBg, { colorSpace } as any) : c.navbarBg) : primaryColor,
    c.navbarOpacity ?? 92,
  );
  const tabBarBg = c.tabBarBg
    ? hexToRgbaWithOpacity(c.tabBarBg.startsWith('#') && colorSpace !== 'Off' ? transformColor(c.tabBarBg, { colorSpace } as any) : c.tabBarBg, c.tabBarOpacity ?? 88)
    : navbarBg;

  const bg = getBackground(
    { primaryColor, secondaryColor, cardBg: cardBg || primaryColor },
    { gradientEnabled: c.gradientEnabled, gradientAngle: c.gradientAngle, gradientFavorability: c.gradientFavorability, gradientStrength: c.gradientStrength },
    primaryColor,
  );

  return {
    '--text-color': textColor,
    '--color-accent': accentColor,
    '--primary-color': primaryColor,
    '--secondary-color': secondaryColor,
    '--card-bg': getCardBackground(
      { primaryColor, secondaryColor, cardBg: cardBg || primaryColor, cardGradientColor1: c.cardGradientColor1, cardGradientColor2: c.cardGradientColor2 },
      { cardGradientEnabled: c.cardGradientEnabled, cardGradientAngle: c.cardGradientAngle, cardGradientFavorability: c.cardGradientFavorability, cardGradientStrength: c.cardGradientStrength, cardOpacity: c.cardOpacity },
      primaryColor,
    ),
    '--navbar-bg': navbarBg,
    '--tab-bar-bg': tabBarBg,
    '--app-bg': bg,
  } as React.CSSProperties;
}

export function MobileCommunityOverlay({ slug }: MobileCommunityOverlayProps) {
  const navigate = useNavigate();
  const { theme: siteTheme } = useTheme();
  const [customization, setCustomization] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Fetch community data to get communityCustomization
  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/communities/slug/${slug}`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const comm = data.community || data;
        if (comm.communityCustomization) {
          setCustomization(comm.communityCustomization);
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [slug]);

  // Listen for community theme updates (from MobileCommunityThemeCustomizer)
  useEffect(() => {
    const handleThemeUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.customization) {
        setCustomization(detail.customization);
      }
    };
    window.addEventListener('communityThemeUpdated', handleThemeUpdate);
    return () => window.removeEventListener('communityThemeUpdated', handleThemeUpdate);
  }, []);

  const themeVars = useMemo(() => buildThemeVars(customization), [customization]);
  const tv = themeVars as Record<string, string>;
  const navBg = tv['--navbar-bg'] || siteTheme.navbarBg || siteTheme.primaryColor;
  const textColor = tv['--text-color'] || siteTheme.textColor || '#fff';

  // Don't render overlay while loading — previous page stays visible with its own theme.
  if (loading) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col"
      style={{ background: 'var(--app-bg)', ...themeVars }}
    >
      {/* Back button header */}
      <div
        className="flex-shrink-0 flex items-center gap-3 px-4"
        style={{
          background: navBg,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          minHeight: 56,
          paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)',
          paddingBottom: 8,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
        >
          <ArrowLeft size={20} style={{ color: textColor }} />
        </button>
        <h2 className="text-base font-semibold truncate" style={{ color: textColor }}>
          {slug}
        </h2>
      </div>

      {/* Community content */}
      <div className="flex-1 overflow-hidden">
        <MobileCommunityClient
          slug={slug}
          activePanel={1}
          onPanelChange={() => {}}
          screenWidth={window.innerWidth}
        />
      </div>
    </div>
  );
}
